/**
 * DocumentMessage Component
 *
 * Renders document/file chat messages with download capability.
 * Supports various document types (PDF, DOC, XLS, etc.) and reply context.
 */

import React, { FC } from "react";
import clsx from "clsx";
import { saveMessageMedia } from "src/actions/chat";
import ChatMessageReplyView from "src/app/layout/chat-handle/components/chat-replymessage/ChatMessageReplyView";
import { DocumentMessageProps } from "../../types/MessageTypes";
import { formatSize } from "src/app/modules/util/Utils";
import Message from "src/app/models/Message";

// Helper to get media URL with fallback
const getMediaUrl = (message: Message): string | undefined => {
  return message.media?.url || message.mediaUrl;
};

// Helper to get filename with fallback
const getFilename = (message: Message): string | undefined => {
  return message.media?.filename || message.filename;
};

// Helper to get formatted file size
const getFileSize = (message: Message): string | null => {
  if (message.media?.size) {
    return formatSize(message.media.size);
  }
  if (message.filesize) {
    return message.filesize;
  }
  return null;
};

const DocumentMessage: FC<DocumentMessageProps> = ({
  message,
  bgChat,
  txChat,
  isTabletOrMobile,
  replyMessageID,
  customer,
  phoneNumber,
  onSave,
}) => {
  const maxWidthClass = isTabletOrMobile ? "mw-300px" : "mw-lg-300px";

  // Get media properties with fallbacks
  const mediaUrl = getMediaUrl(message);
  const filename = getFilename(message);
  const fileSize = getFileSize(message);

  // Truncate filename for display
  const displayFilename =
    filename && filename.length >= 25
      ? filename.slice(0, 25) + "..."
      : filename || "Document";

  /**
   * Handles save/download action
   */
  const handleSave = () => {
    if (onSave) {
      onSave();
    } else {
      // Pass message object to get proper filename with extension
      saveMessageMedia(message);
    }
  };

  /**
   * Renders the document content
   */
  const renderDocument = () => (
    <div
      className={clsx("rounded", bgChat, "d-flex flex-column", maxWidthClass)}
    >
      <div
        className={clsx(
          "p-2 rounded",
          bgChat,
          "d-flex flex-row align-items-center bg-opacity-25"
        )}
        title={filename}
      >
        {/* Document icon */}
        <div
          className="bi bi-file-earmark-text fs-3x"
          style={{ color: txChat }}
        />

        {/* Filename and size */}
        <div
          className="container-sm d-flex flex-column"
          style={{ paddingLeft: "5px" }}
        >
          <span style={{ color: txChat }}>{displayFilename}</span>
          {fileSize && (
            <span className="text-muted" style={{ fontSize: "11px" }}>
              {fileSize}
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div
          className="d-flex flex-column justify-content-end"
          style={{ width: "70px" }}
        >
          {/* Download button */}
          <button
            className="btn btn-sm btn-icon btn-light-primary"
            onClick={handleSave}
            title="Download"
          >
            <i className="bi bi-download" />
          </button>
        </div>
      </div>

      {/* Caption if present */}
      {message.textContent && message.textContent !== "" && (
        <div className={clsx("p-2 rounded", bgChat)} style={{ color: txChat }}>
          {message.textContent}
        </div>
      )}
    </div>
  );

  /**
   * Renders with reply context
   */
  const renderWithReply = () => (
    <ChatMessageReplyView
      bgChat={bgChat}
      replyMessageID={replyMessageID}
      customer={customer}
      phoneNumber={phoneNumber}
    >
      {renderDocument()}
    </ChatMessageReplyView>
  );

  // Render with or without reply context
  if (message.replyMessageID) {
    return renderWithReply();
  }

  return renderDocument();
};

export default DocumentMessage;
